import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Leaf } from 'lucide-react';
import { assets } from '../lib/assets';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';

// Full founder story page. Typography-first, reads like a letter.
const paragraphs = [
  { kind: 'lead', text: 'Some stories are not planned.\nThey grow quietly — like seeds waiting for the right season.' },
  { kind: 'p', text: 'Pallavit began in such a space.' },
  { kind: 'p', text: 'Long before it became a name, it was a feeling tied to a piece of land near Kalwan, Nashik — a land Pallavi has known not as property, but as presence.' },
  { kind: 'p', text: 'She grew up walking its soil, watching leaves turn, noting the cadence of seasons, listening to its silences. For her, nature was never separate from life — it was the canvas itself.' },
  { kind: 'p', text: 'An artist by soul and profession, Pallavi has spent years painting life as she sees it — layered, evolving, deeply felt. Her work has always drawn from the same roots: the fields, the light, the slow time of the orchard she grew up in.' },
  { kind: 'quote', text: 'The same sensitivity lives in the land she belongs to.' },
  { kind: 'divider' },
  { kind: 'p', text: "Shivangi's journey began far from this quiet." },
  { kind: 'p', text: 'She built her voice in newsrooms and studios as a journalist and anchor — reporting for DD National, News 18 India, Punjab Kesari and Dailyhunt. Asking questions, chasing stories, staying in motion.' },
  { kind: 'p', text: 'Yet somewhere within that movement, a stillness kept calling her back.' },
  { kind: 'p', text: 'A pull toward nature.\nToward creation.\nToward something more lasting than headlines.' },
  { kind: 'p', text: 'She began to feel that while the world was becoming faster and louder, people were slowly losing touch with something essential — their connection to nature, and with it, a softness within themselves.' },
  { kind: 'p', text: 'Where Pallavi had always lived close to the soil, Shivangi felt a need to bring people back to it.' },
  { kind: 'divider' },
  { kind: 'p', text: 'When their paths aligned, the idea did not arrive suddenly. It unfolded — gently, honestly.' },
  { kind: 'quote', text: 'A shared question lingered between them:\nWhat if a gift didn\u2019t end… but continued to grow?' },
  { kind: 'p', text: 'And so, on this land — already rich with memory and care — they began planting trees in the names of people.' },
  { kind: 'p', text: 'At first, it was simple.\nA tree.  A name.  A moment.' },
  { kind: 'p', text: 'But slowly, something deeper revealed itself.' },
  { kind: 'p', text: "People didn't just gift trees. They formed relationships with them." },
  { kind: 'p', text: 'They waited for them.\nVisited them.\nSpoke of them.\nReceived their fruits.' },
  { kind: 'quote', text: 'What was planted in the soil began to grow in their lives.' },
  { kind: 'divider' },
  { kind: 'p', text: 'Pallavit was never meant to be just a project.' },
  { kind: 'p', text: 'It is a meeting point — between art and earth, between stories and silence, between people and the part of them that still longs to feel connected.' },
  { kind: 'tree' },
  { kind: 'p', text: 'Together, Shivangi and Pallavi are not just building something — they are nurturing it.' },
  { kind: 'p', text: 'With patience.\nWith honesty.\nWith the belief that when we return to nature, we return to ourselves.' },
  { kind: 'closing', text: 'And sometimes,\nall it takes…\nis planting a tree.' },
];

export default function FoundersPage() {
  React.useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-brand-bg text-brand-ink min-h-screen" data-testid="founders-page">
      {/* Slim header */}
      <div className="sticky top-0 z-30 bg-brand-bg border-b border-brand-line">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-brand-ink hover:text-brand-forest transition-colors" data-testid="founders-back-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm tracking-wide">Back to Pallavit</span>
          </Link>
          <img
            src={assets.logo}
            alt="Pallavit"
            className="h-12 w-auto object-contain"
            style={{ mixBlendMode: 'multiply' }}
          />
        </div>
      </div>

      {/* Hero */}
      <section className="relative pt-20 md:pt-28 pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <p className="text-brand-gold tracking-[0.25em] uppercase text-xs mb-6">🌿  Rooted by</p>
            <h1 className="font-serif text-5xl sm:text-7xl lg:text-[96px] text-brand-ink leading-[1.02] tracking-tight font-light">
              Shivangi <span className="italic text-brand-gold">&</span> Pallavi
            </h1>
            <p className="mt-6 text-brand-ink2 font-serif italic text-lg md:text-xl max-w-xl mx-auto leading-relaxed">
              The two women whose quiet shared question grew into an orchard.
            </p>
          </motion.div>
        </div>

        {/* Founder portraits */}
        <div className="max-w-5xl mx-auto px-6 md:px-10 mt-16 grid md:grid-cols-2 gap-6">
          <motion.figure
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.15 }}
            className="relative rounded-[28px] overflow-hidden aspect-[4/5]"
          >
            <img src={assets.pallaviPortrait} alt="Pallavi" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/65 via-brand-ink/10 to-transparent" />
            <figcaption className="absolute left-7 bottom-7 text-brand-bg">
              <div className="font-serif text-4xl leading-none mb-2">Pallavi</div>
              <div className="text-brand-gold italic font-serif text-sm">Artist</div>
            </figcaption>
          </motion.figure>
          <motion.figure
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="relative rounded-[28px] overflow-hidden aspect-[4/5] md:mt-16"
          >
            <img
              src={assets.shivangiPortrait}
              alt="Shivangi"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: '50% 18%', transform: 'scale(1.35)', transformOrigin: '50% 18%' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/65 via-brand-ink/10 to-transparent" />
            <figcaption className="absolute left-7 bottom-7 text-brand-bg">
              <div className="font-serif text-4xl leading-none mb-2">Shivangi</div>
              <div className="text-brand-gold italic font-serif text-sm">Journalist</div>
            </figcaption>
          </motion.figure>
        </div>
      </section>

      {/* Long-form story */}
      <section className="pb-24">
        <article className="max-w-2xl mx-auto px-6 md:px-10 font-serif">
          {paragraphs.map((p, i) => {
            if (p.kind === 'divider') {
              return (
                <div key={i} className="flex items-center justify-center my-10" aria-hidden>
                  <span className="inline-block w-2 h-2 rounded-full bg-brand-gold" />
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-gold/60 mx-3" />
                  <span className="inline-block w-2 h-2 rounded-full bg-brand-gold" />
                </div>
              );
            }
            if (p.kind === 'tree') {
              return <div key={i} className="text-center text-4xl my-10">🌳</div>;
            }
            if (p.kind === 'lead') {
              return (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className="italic text-xl md:text-2xl text-brand-forest text-center leading-relaxed mb-10 whitespace-pre-line"
                >
                  {p.text}
                </motion.p>
              );
            }
            if (p.kind === 'quote') {
              return (
                <motion.blockquote
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className="border-l-2 border-brand-gold pl-6 my-8 italic text-xl md:text-2xl text-brand-ink leading-relaxed whitespace-pre-line"
                >
                  {p.text}
                </motion.blockquote>
              );
            }
            if (p.kind === 'closing') {
              return (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className="italic text-2xl md:text-3xl text-brand-forest text-center leading-relaxed my-12 whitespace-pre-line"
                >
                  {p.text}
                </motion.p>
              );
            }
            return (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.6 }}
                className="text-lg md:text-xl text-brand-ink2 leading-[1.9] my-5 whitespace-pre-line"
              >
                {p.text}
              </motion.p>
            );
          })}

          <div className="mt-14 text-center">
            <Link
              to="/"
              data-testid="founders-cta-gift"
              className="inline-flex rounded-full bg-brand-forest text-brand-bg px-8 py-3.5 text-sm tracking-wide font-medium hover:bg-brand-ink transition-all"
            >
              Return to the orchard →
            </Link>
          </div>
        </article>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
